import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AUTHORIZATION_ROLES, type JoinRequestList as JoinRequestAnswer, type JoinRequestRow } from '@scoutaround/script-breakdown-shared';

import { api, ApiError, type Invitation, type Member } from '../../lib/api.js';
import { useMembership, useRefusal } from '../../lib/permissions.js';
import { Callout } from '../../components/Callout.js';
import { Icon, type IconName } from '../../components/Icon.js';
import { RolePermissionsGrid } from '../../components/RolePermissionsGrid.js';
import { useConversationsEnabled, usePeople } from '../../components/conversations/hooks.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { SearchField } from '../../components/ui/SearchField.js';
import { SegmentedFilter, type FilterOption } from '../../components/ui/SegmentedFilter.js';
import { InviteDrawer, type InviteMethod, type InvitePrefill } from '../../components/team/InviteDrawer.js';
import {
  ApproveJoinDialog,
  JoinRequestList,
  RejectJoinDialog,
  requesterName,
} from '../../components/team/JoinRequestList.js';
import { InvitationRow, type InvitationActions, type InvitationRights } from '../../components/team/InvitationList.js';
import { MemberList, memberName } from '../../components/team/MemberList.js';
import { SideDialog } from '../../components/team/SideDialog.js';
import {
  RoleSelect,
  invitationState,
  inviteRefusal,
  roleLabel,
  roleMeaning,
  type InvitationState,
} from '../../components/team/roles.js';
import '../../styles/team.css';

/**
 * YOUR PRODUCTION TEAM — people first, administration second.
 *
 * The page used to be one long column doing three jobs: the team, the invite
 * form with every invitation ever sent, and the 28-role permission grid. Those
 * are three different visits, so they are three views of one page (owner brief
 * "Team Page UX/UI Redesign", 2026-11-02):
 *
 * - **Members** (default): who is on the production and what their job lets
 *   them do, the invitations still waiting, and the way to the role settings.
 * - **Invitations**: every invitation and where it stands, with what can be
 *   done about it.
 * - **Roles & access**: what each role may do on this production — the grid,
 *   unchanged.
 *
 * ONE primary action on every view: Invite teammate, top right. Inviting opens
 * a panel instead of sitting on the page as a form. The view, the member search,
 * the invitation filter and the open invite panel are in the address
 * (`?view=&q=&filter=&invite=1`), like every other state worth sending.
 *
 * Nothing the server does changed: the same verbs, the same permission cells
 * (`TEAM_MANAGE` to invite and change a role, `INVITATION_REVOKE` to revoke,
 * `READ_TEAM` to read), the same collaboration gate on inviting.
 *
 * INVITATION LINKS (owner brief 2026-11-02): the invite panel offers a second
 * method, "Share invite link" (`?invite=1&method=link`), and the Invitations view
 * two lists, "Email invitations" and "Join requests" (`?list=requests`), where the
 * production's owner approves each request with a role or rejects it. The count
 * on the Invitations tab adds the requests waiting for that decision.
 */

type View = 'members' | 'invitations' | 'roles';

const VIEWS: readonly { id: View; label: string; icon: IconName }[] = [
  { id: 'members', label: 'Members', icon: 'team' },
  { id: 'invitations', label: 'Invitations', icon: 'mail' },
  { id: 'roles', label: 'Roles & access', icon: 'shield' },
];

function parseView(raw: string | null): View {
  return raw === 'invitations' || raw === 'roles' ? raw : 'members';
}

type InvitationFilter = 'all' | 'pending' | 'expired' | 'accepted' | 'revoked';

const FILTER_STATE: Readonly<Record<Exclude<InvitationFilter, 'all'>, InvitationState>> = {
  pending: 'PENDING',
  expired: 'EXPIRED',
  accepted: 'ACCEPTED',
  revoked: 'REVOKED',
};

function parseFilter(raw: string | null): InvitationFilter {
  return raw === 'pending' || raw === 'expired' || raw === 'accepted' || raw === 'revoked' ? raw : 'all';
}

/** The Invitations view's two lists. */
type InvitationList = 'email' | 'requests';

function parseList(raw: string | null): InvitationList {
  return raw === 'requests' ? 'requests' : 'email';
}

type RequestFilter = 'pending' | 'decided' | 'all';

function parseRequestFilter(raw: string | null): RequestFilter {
  return raw === 'decided' || raw === 'all' ? raw : 'pending';
}

function parseMethod(raw: string | null): InviteMethod {
  return raw === 'link' ? 'link' : 'email';
}

/** How many invitations the Members view lists before "View all". */
const WAITING_SHOWN = 4;

interface Notice {
  readonly tone: 'ok' | 'danger';
  readonly title: string;
  readonly body: string;
  readonly testId: string;
  /** A failed new link left the address with no invitation: offer to invite it again. */
  readonly reinvite?: Invitation;
}

/** A new link is two verbs; which one failed decides what the producer is told. */
class NewLinkFailure extends Error {
  constructor(
    readonly stage: 'revoke' | 'invite',
    readonly reason: unknown,
  ) {
    super(stage);
  }
}

function apiReason(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

export function TeamTab({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const baseId = useId();

  const view = parseView(params.get('view'));
  const query = params.get('q') ?? '';
  const filter = parseFilter(params.get('filter'));
  const roleParam = params.get('role');
  const list = parseList(params.get('list'));
  const requestFilter = parseRequestFilter(params.get('requests'));
  const method = parseMethod(params.get('method'));

  const membership = useMembership();
  const me = membership?.userId ?? null;
  const manageRefusal = useRefusal('TEAM_MANAGE');
  const revokeRefusal = useRefusal('INVITATION_REVOKE');

  const members = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => api.listMembers(projectId),
  });
  const invitations = useQuery({
    queryKey: ['invitations', projectId],
    queryFn: () => api.listInvitations(projectId),
  });
  // Requests made through the invitation link (2026-11-02). Read like the
  // invitations (`READ_TEAM`); only the owner may decide them.
  const joinRequests = useQuery({
    queryKey: ['join-requests', projectId],
    queryFn: () => api.joinRequests(projectId),
    retry: false,
  });
  // Names from the platform's directory (ADR 0021); a placeholder while one cannot be read.
  const people = usePeople(projectId);
  const conversationsOn = useConversationsEnabled();
  // The same answer the workspace shell reads on every tab (same key). Unknown
  // reads as allowed: the server's FEATURE_NOT_IN_PLAN still says it if so.
  const billing = useQuery({
    queryKey: ['billing', projectId],
    queryFn: () => api.billingState(projectId),
    enabled: projectId !== '',
  });
  const planAllowsInvites = billing.data?.features.collaboration !== false;

  /**
   * The permission grid. Reading it needs `READ_TEAM` and changing it
   * `TEAM_MANAGE`; asked for only on its own view.
   */
  const rolePermissions = useQuery({
    queryKey: ['role-permissions', projectId],
    queryFn: () => api.rolePermissions(projectId),
    retry: false,
    enabled: view === 'roles',
  });

  const [notice, setNotice] = useState<Notice | null>(null);
  const [prefill, setPrefill] = useState<InvitePrefill | null>(null);
  const [changing, setChanging] = useState<Member | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [renewing, setRenewing] = useState<Invitation | null>(null);
  const [approving, setApproving] = useState<JoinRequestRow | null>(null);
  const [rejecting, setRejecting] = useState<JoinRequestRow | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  /**
   * Where focus goes back to when a dialog opened from a ⋯ menu closes. The
   * menu item that opened it is gone by then, so the dialog's own "return to
   * the opener" finds nothing; the menu's button is the place the person was.
   */
  const [returnFocus, setReturnFocus] = useState<string | null>(null);

  const inviting = params.get('invite') === '1' && manageRefusal === null;

  useEffect(() => {
    if (returnFocus === null || changing !== null || renewing !== null || approving !== null || rejecting !== null || inviting) return;
    document.querySelector<HTMLElement>(`[data-testid="${returnFocus}"]`)?.focus();
    setReturnFocus(null);
  }, [returnFocus, changing, renewing, approving, rejecting, inviting]);

  const update = (change: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    change(next);
    setParams(next, { replace: true });
  };

  const setView = (next: View, role?: string) => {
    update((p) => {
      if (next === 'members') p.delete('view');
      else p.set('view', next);
      if (role === undefined) p.delete('role');
      else p.set('role', role);
    });
  };

  const openInvite = (from: InvitePrefill | null = null) => {
    setPrefill(from);
    update((p) => {
      p.set('invite', '1');
    });
  };

  const closeInvite = () => {
    update((p) => {
      p.delete('invite');
      p.delete('method');
    });
  };

  const setMethod = (next: InviteMethod) => {
    update((p) => {
      if (next === 'email') p.delete('method');
      else p.set('method', next);
    });
  };

  const showRequests = () => {
    update((p) => {
      p.set('view', 'invitations');
      p.set('list', 'requests');
      p.delete('role');
    });
  };

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['members', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['invitations', projectId] }),
    ]);
  };

  const setPermission = useMutation({
    mutationFn: (cell: { role: string; permission: string; state: 'GRANTED' | 'REVOKED' | 'DEFAULT' }) =>
      api.setRolePermission(projectId, cell),
    onSuccess: async (grid) => {
      setPermissionError(null);
      queryClient.setQueryData(['role-permissions', projectId], grid);
      // A permission change moves what the team list reports each member can do.
      await queryClient.invalidateQueries({ queryKey: ['members', projectId] });
    },
    onError: (e: unknown) => {
      setPermissionError(apiReason(e, 'That permission could not be changed.'));
    },
  });

  const revoke = useMutation({
    mutationFn: (invitation: Invitation) => api.revokeInvitation(projectId, invitation.id),
    onSuccess: async (_result, invitation) => {
      setNotice({
        tone: 'ok',
        title: 'Invitation revoked',
        body: `The link sent to ${invitation.email} no longer works.`,
        testId: 'invitation-revoked',
      });
      await refresh();
    },
    onError: async (e: unknown) => {
      setNotice({
        tone: 'danger',
        title: 'Not revoked',
        body: apiReason(e, 'That invitation could not be revoked.'),
        testId: 'invitation-error',
      });
      await refresh();
    },
  });

  /**
   * A NEW LINK, from the two verbs the server has: revoke this invitation, then
   * invite the same address for the same job and credit. There is no resend;
   * the earlier link stops working, and the screen says so.
   */
  const renew = useMutation({
    mutationFn: async (invitation: Invitation) => {
      try {
        await api.revokeInvitation(projectId, invitation.id);
      } catch (e) {
        throw new NewLinkFailure('revoke', e);
      }
      try {
        return await api.invite(projectId, {
          email: invitation.email,
          authorizationRole: invitation.authorizationRole,
          ...(invitation.productionTitle ? { productionTitle: invitation.productionTitle } : {}),
        });
      } catch (e) {
        throw new NewLinkFailure('invite', e);
      }
    },
    onSuccess: async (result) => {
      setRenewing(null);
      setNotice({
        tone: 'ok',
        title: 'New link sent',
        body: `We emailed ${result.email} a new invitation. The link in the earlier email no longer works.`,
        testId: 'renew-sent',
      });
      await refresh();
    },
    onError: async (e: unknown, invitation) => {
      setRenewing(null);
      if (e instanceof NewLinkFailure && e.stage === 'invite') {
        setNotice({
          tone: 'danger',
          title: 'The earlier link was revoked, but no new one went out',
          body: `${inviteRefusal(e.reason)} Invite ${invitation.email} again to send a new link.`,
          testId: 'renew-error',
          reinvite: invitation,
        });
      } else {
        setNotice({
          tone: 'danger',
          title: 'No new link sent',
          body: `${apiReason(e instanceof NewLinkFailure ? e.reason : e, 'That invitation could not be revoked.')} The earlier invitation is unchanged.`,
          testId: 'renew-error',
        });
      }
      await refresh();
    },
  });

  const decided = async () => {
    setApproving(null);
    setRejecting(null);
    setDecisionError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['join-requests', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['members', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project-people', projectId] }),
      // The Overview's "people waiting to join" line counts what is left.
      queryClient.invalidateQueries({ queryKey: ['project-overview', projectId] }),
    ]);
  };

  const approve = useMutation({
    mutationFn: (input: { request: JoinRequestRow; role: string }) =>
      api.approveJoinRequest(projectId, input.request.id, input.role),
    onSuccess: async (result, input) => {
      const name = requesterName(input.request);
      setNotice(
        result.alreadyMember
          ? {
              tone: 'ok',
              title: 'Request closed',
              body: `${name} was already on the team; their role did not change. Change it under Members if you need to.`,
              testId: 'join-approved',
            }
          : result.status === 'APPROVED_AWAITING_ACTIVATION'
            ? {
                tone: 'ok',
                title: 'Approved — waiting for activation',
                body: `${name} is approved as ${roleLabel(input.role)}. ScoutAround has not enabled the Production workspace for their account yet; they join the team as soon as it is, and nothing is open to them before then. We emailed them.`,
                testId: 'join-approved',
              }
            : {
                tone: 'ok',
                title: 'Request approved',
                body: `${name} joined the team as ${roleLabel(input.role)}. We emailed them a way in.`,
                testId: 'join-approved',
              },
      );
      await decided();
    },
    onError: async (e: unknown) => {
      setDecisionError(apiReason(e, 'That request could not be approved.'));
      await queryClient.invalidateQueries({ queryKey: ['join-requests', projectId] });
    },
  });

  const reject = useMutation({
    mutationFn: (request: JoinRequestRow) => api.rejectJoinRequest(projectId, request.id),
    onSuccess: async (_result, request) => {
      setNotice({
        tone: 'ok',
        title: 'Request rejected',
        body: `${requesterName(request)} was told by email and has no access. The invitation link keeps working for everyone else.`,
        testId: 'join-rejected',
      });
      await decided();
    },
    onError: async (e: unknown) => {
      setDecisionError(apiReason(e, 'That request could not be rejected.'));
      await queryClient.invalidateQueries({ queryKey: ['join-requests', projectId] });
    },
  });

  const changeRole = useMutation({
    mutationFn: (input: { member: Member; role: string }) => api.setMemberRole(projectId, input.member.id, input.role),
    onSuccess: async (_result, input) => {
      setChanging(null);
      setRoleError(null);
      const name = memberName(people.data?.people, input.member.userId) ?? 'This member';
      setNotice({
        tone: 'ok',
        title: 'Role changed',
        body: `${name} is now ${roleLabel(input.role)} on this production.`,
        testId: 'role-changed',
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['members', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project-people', projectId] }),
        // Changing your own role changes what this browser should offer you.
        queryClient.invalidateQueries({ queryKey: ['my-membership', projectId] }),
      ]);
    },
    onError: (e: unknown) => {
      setRoleError(apiReason(e, 'That role could not be changed.'));
    },
  });

  const now = Date.now();
  const allInvitations = invitations.data ?? [];
  const stateOf = (invitation: Invitation) => invitationState(invitation, now);
  const waiting = allInvitations.filter((i) => {
    const s = stateOf(i);
    return s === 'PENDING' || s === 'EXPIRED';
  });

  const canInvite = manageRefusal === null && planAllowsInvites;
  const planRefusal = planAllowsInvites ? null : 'Inviting is included from Lite upwards — see Plan & billing.';
  const rights: InvitationRights = {
    renewRefusal: manageRefusal ?? revokeRefusal ?? planRefusal,
    revokeRefusal,
    inviteRefusal: manageRefusal ?? planRefusal,
  };
  const invitationActions: InvitationActions = {
    onRenew: (invitation) => {
      setReturnFocus(`renew-${invitation.id}`);
      setRenewing(invitation);
    },
    onRevoke: (invitation) => {
      setReturnFocus(`invitation-menu-${invitation.id}`);
      revoke.mutate(invitation);
    },
    onInviteAgain: (invitation) => {
      setReturnFocus(`invitation-menu-${invitation.id}`);
      openInvite({ email: invitation.email, role: invitation.authorizationRole, credit: invitation.productionTitle });
    },
  };

  const requestRows = joinRequests.data?.requests ?? [];
  const pendingRequests = requestRows.filter((r) => r.status === 'PENDING_APPROVAL');

  const counts: Partial<Record<View, number>> = {
    ...(members.data === undefined ? {} : { members: members.data.length }),
    // Everything that waits on the team: open email invitations, and requests
    // waiting for the owner's decision (left out, never drawn as 0, while unknown).
    ...(invitations.data === undefined ? {} : { invitations: waiting.length + pendingRequests.length }),
  };

  const header = (
    <PageHeader
      testId="team-header"
      title="Your production team"
      lede="Bring the people working on this production together."
      actions={
        <button
          type="button"
          className="btn btn-primary"
          data-testid="team-invite-open"
          disabled={manageRefusal !== null}
          title={manageRefusal ?? undefined}
          onClick={() => {
            openInvite();
          }}
        >
          <Icon name="userPlus" size={18} />
          Invite teammate
        </button>
      }
    />
  );

  return (
    <div data-testid="team-tab" className="team-page">
      {header}
      {manageRefusal === null ? null : (
        <p className="team-hint" data-testid="team-invite-refusal">
          {manageRefusal}
        </p>
      )}

      {notice === null ? null : (
        <Callout
          tone={notice.tone}
          title={notice.title}
          testId={notice.testId}
          action={
            <div className="team-notice-actions">
              {notice.reinvite === undefined ? null : (
                <button
                  type="button"
                  className="ctl"
                  data-testid="notice-invite-again"
                  onClick={() => {
                    const again = notice.reinvite;
                    if (again === undefined) return;
                    openInvite({ email: again.email, role: again.authorizationRole, credit: again.productionTitle });
                  }}
                >
                  Invite again
                </button>
              )}
              <button
                type="button"
                className="ctl"
                aria-label="Dismiss"
                data-testid="notice-dismiss"
                onClick={() => {
                  setNotice(null);
                }}
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          }
        >
          {notice.body}
        </Callout>
      )}

      <TeamTabs
        baseId={baseId}
        view={view}
        counts={counts}
        onChange={(next) => {
          setView(next);
        }}
      />

      <div
        role="tabpanel"
        id={`${baseId}-panel`}
        aria-labelledby={`${baseId}-tab-${view}`}
        data-testid={`team-panel-${view}`}
        className="team-panel"
      >
        {view === 'members' ? (
          <div className="team-members-layout">
            {canInvite ? (
              <div className="team-banner" data-testid="team-collab-banner">
                <span className="team-banner-icon" aria-hidden="true">
                  <Icon name="team" size={18} />
                </span>
                <p>Teams that collaborate in ScoutAround review scenes, shortlist locations and make faster decisions together.</p>
              </div>
            ) : null}

            <section className="ui-panel team-members-panel" data-testid="team-members" aria-labelledby={`${baseId}-members`}>
              <div className="ui-panel-head">
                <h3 className="ui-panel-title" id={`${baseId}-members`}>
                  Team members
                </h3>
                <SearchField
                  value={query}
                  placeholder="Search teammates…"
                  label="Search teammates"
                  testId="team-search"
                  onChange={(next) => {
                    update((p) => {
                      if (next === '') p.delete('q');
                      else p.set('q', next);
                    });
                  }}
                />
              </div>
              <MembersBody
                members={members}
                query={query}
                people={people.data?.people}
                me={me}
                manageRefusal={manageRefusal}
                canInvite={canInvite}
                onInvite={() => {
                  openInvite();
                }}
                onClearSearch={() => {
                  update((p) => {
                    p.delete('q');
                  });
                }}
                onChangeRole={(member) => {
                  setReturnFocus(`member-menu-${member.id}`);
                  setRoleError(null);
                  setChanging(member);
                }}
                onSeeRole={(role) => {
                  setView('roles', role);
                }}
              />
            </section>

            <section className="ui-panel team-waiting-panel" data-testid="team-waiting" aria-labelledby={`${baseId}-waiting`}>
              <div className="ui-panel-head">
                <h3 className="ui-panel-title" id={`${baseId}-waiting`}>
                  Pending invitations
                </h3>
                {waiting.length > WAITING_SHOWN ? (
                  <button
                    type="button"
                    className="ctl"
                    data-testid="team-waiting-all"
                    onClick={() => {
                      setView('invitations');
                    }}
                  >
                    View all {String(waiting.length)}
                  </button>
                ) : null}
              </div>
              {invitations.isPending ? (
                <p className="team-quiet">Loading invitations…</p>
              ) : invitations.isError ? (
                <p className="team-quiet" data-testid="team-waiting-error">
                  {apiReason(invitations.error, 'The invitations could not be loaded.')}
                </p>
              ) : waiting.length === 0 && pendingRequests.length === 0 ? (
                <p className="team-quiet" data-testid="team-waiting-empty">
                  Nobody is waiting to join.
                  {allInvitations.length > 0 ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="team-link"
                        data-testid="team-waiting-history"
                        onClick={() => {
                          setView('invitations');
                        }}
                      >
                        See earlier invitations
                      </button>
                    </>
                  ) : null}
                </p>
              ) : waiting.length === 0 ? null : (
                <ul className="team-invite-list">
                  {waiting.slice(0, WAITING_SHOWN).map((invitation) => (
                    <InvitationRow
                      key={invitation.id}
                      invitation={invitation}
                      now={now}
                      rights={rights}
                      actions={invitationActions}
                      compact
                    />
                  ))}
                </ul>
              )}
              {/* Requests through the link, as ONE line: the list is under Invitations. */}
              {pendingRequests.length === 0 ? null : (
                <div className="team-requests-line" data-testid="team-waiting-requests">
                  <span className="team-invite-icon" aria-hidden="true">
                    <Icon name="link" size={18} />
                  </span>
                  <p>
                    {pendingRequests.length === 1
                      ? '1 person asked to join with your invitation link.'
                      : `${String(pendingRequests.length)} people asked to join with your invitation link.`}
                  </p>
                  <button type="button" className="ctl" data-testid="team-waiting-requests-open" onClick={showRequests}>
                    Review requests
                  </button>
                </div>
              )}
            </section>

            <section className="team-roles-card" data-testid="team-roles-card">
              <span className="team-roles-card-icon" aria-hidden="true">
                <Icon name="shield" size={20} />
              </span>
              <div className="team-roles-card-text">
                <h3>Roles &amp; access</h3>
                <p>Choose what each role can see and do on this production.</p>
              </div>
              <button
                type="button"
                className="ctl"
                data-testid="team-roles-open"
                onClick={() => {
                  setView('roles');
                }}
              >
                Manage roles &amp; access
              </button>
            </section>
          </div>
        ) : null}

        {view === 'invitations' ? (
          <SegmentedFilter<InvitationList>
            label="Which invitations"
            testIdPrefix="invitations-list"
            value={list}
            options={[
              { id: 'email', label: 'Email invitations', ...(invitations.data === undefined ? {} : { count: allInvitations.length }) },
              {
                id: 'requests',
                label: 'Join requests',
                ...(joinRequests.data === undefined ? {} : { count: pendingRequests.length }),
                ...(pendingRequests.length > 0 ? { tone: 'attention' as const } : {}),
              },
            ]}
            onChange={(next) => {
              update((p) => {
                if (next === 'email') p.delete('list');
                else p.set('list', next);
              });
            }}
          />
        ) : null}

        {view === 'invitations' && list === 'requests' ? (
          <section className="ui-panel" data-testid="team-join-requests" aria-labelledby={`${baseId}-requests`}>
            <div className="ui-panel-head">
              <h3 className="ui-panel-title" id={`${baseId}-requests`}>
                Join requests
              </h3>
              <SegmentedFilter<RequestFilter>
                label="Show requests"
                testIdPrefix="join-requests-filter"
                value={requestFilter}
                options={requestFilterOptions(joinRequests.data?.requests)}
                onChange={(next) => {
                  update((p) => {
                    if (next === 'pending') p.delete('requests');
                    else p.set('requests', next);
                  });
                }}
              />
            </div>
            <JoinRequestsBody
              query={joinRequests}
              filter={requestFilter}
              onApprove={(request) => {
                setReturnFocus(`join-request-approve-${request.id}`);
                setDecisionError(null);
                setApproving(request);
              }}
              onReject={(request) => {
                setReturnFocus(`join-request-reject-${request.id}`);
                setDecisionError(null);
                setRejecting(request);
              }}
              onShareLink={
                manageRefusal === null
                  ? () => {
                      update((p) => {
                        p.set('invite', '1');
                        p.set('method', 'link');
                      });
                    }
                  : null
              }
            />
          </section>
        ) : null}

        {view === 'invitations' && list === 'email' ? (
          <section className="ui-panel" data-testid="team-invitations" aria-labelledby={`${baseId}-invitations`}>
            <div className="ui-panel-head">
              <h3 className="ui-panel-title" id={`${baseId}-invitations`}>
                Email invitations
              </h3>
              <SegmentedFilter<InvitationFilter>
                label="Show invitations"
                testIdPrefix="invitations-filter"
                value={filter}
                options={filterOptions(invitations.data, now)}
                onChange={(next) => {
                  update((p) => {
                    if (next === 'all') p.delete('filter');
                    else p.set('filter', next);
                  });
                }}
              />
            </div>
            <InvitationsBody
              invitations={invitations}
              filter={filter}
              now={now}
              rights={rights}
              actions={invitationActions}
              canInvite={canInvite}
              onInvite={() => {
                openInvite();
              }}
            />
          </section>
        ) : null}

        {view === 'roles' ? (
          <section data-testid="team-roles" className="team-roles">
            {manageRefusal === null ? null : (
              <p className="team-hint" data-testid="team-roles-refusal">
                You can see these settings; changing them is up to whoever manages the team. {manageRefusal}
              </p>
            )}
            {rolePermissions.data ? (
              <RolePermissionsGrid
                key={roleParam ?? ''}
                grid={rolePermissions.data}
                busy={setPermission.isPending}
                error={permissionError}
                conversationsOn={conversationsOn}
                {...(roleParam === null ? {} : { initialRole: roleParam })}
                onChange={(cell) => {
                  setPermission.mutate(cell);
                }}
              />
            ) : rolePermissions.isError ? (
              <EmptyState testId="team-roles-unavailable" title="The role settings are not available to you here">
                {apiReason(rolePermissions.error, 'They could not be loaded.')}
              </EmptyState>
            ) : (
              <p className="team-quiet" data-testid="team-roles-loading">
                Loading the role settings…
              </p>
            )}
          </section>
        ) : null}
      </div>

      {inviting ? (
        <InviteDrawer
          key={prefill === null ? 'blank' : `again:${prefill.email}`}
          projectId={projectId}
          prefill={prefill}
          planAllowsInvites={planAllowsInvites}
          // "Invite again" is an email by definition; the link is one per production.
          method={prefill === null ? method : 'email'}
          onMethod={setMethod}
          onClose={closeInvite}
          onSent={async (email) => {
            closeInvite();
            setPrefill(null);
            // THE TOKEN IS NOT HERE. The link goes to the invitee and nowhere
            // else; the producer is told where it went and for how long.
            setNotice({
              tone: 'ok',
              title: 'Invitation sent',
              body: `We emailed ${email}. The email tells them how to join, and that ScoutAround has to enable the Production workspace for their account first if it is not already. The link is valid for fourteen days; you can revoke it under Invitations.`,
              testId: 'invite-sent',
            });
            await refresh();
          }}
        />
      ) : null}

      {changing === null ? null : (
        <ChangeRoleDialog
          member={changing}
          name={memberName(people.data?.people, changing.userId) ?? `Member #${String(changing.userId)}`}
          pending={changeRole.isPending}
          error={roleError}
          onSave={(role) => {
            changeRole.mutate({ member: changing, role });
          }}
          onClose={() => {
            setChanging(null);
            setRoleError(null);
          }}
        />
      )}

      {approving === null ? null : (
        <ApproveJoinDialog
          request={approving}
          pending={approve.isPending}
          error={decisionError}
          onApprove={(role) => {
            approve.mutate({ request: approving, role });
          }}
          onClose={() => {
            if (!approve.isPending) {
              setApproving(null);
              setDecisionError(null);
            }
          }}
        />
      )}

      {rejecting === null ? null : (
        <RejectJoinDialog
          request={rejecting}
          pending={reject.isPending}
          error={decisionError}
          onReject={() => {
            reject.mutate(rejecting);
          }}
          onClose={() => {
            if (!reject.isPending) {
              setRejecting(null);
              setDecisionError(null);
            }
          }}
        />
      )}

      {renewing === null ? null : (
        <SideDialog
          title="Send a new link"
          testId="renew-dialog"
          onClose={() => {
            if (!renew.isPending) setRenewing(null);
          }}
        >
          <p className="team-dialog-lede" data-testid="renew-explainer">
            We will email {renewing.email} a new invitation as {roleLabel(renewing.authorizationRole)}
            {renewing.productionTitle ? ` (${renewing.productionTitle})` : ''}. The link in the earlier email stops
            working, and the new one is valid for fourteen days.
          </p>
          <div className="team-dialog-actions">
            <button
              type="button"
              className="btn btn-primary"
              data-testid="renew-confirm"
              data-autofocus
              disabled={renew.isPending}
              onClick={() => {
                renew.mutate(renewing);
              }}
            >
              {renew.isPending ? 'Sending…' : 'Send new link'}
            </button>
            <button
              type="button"
              className="ctl"
              data-testid="renew-cancel"
              disabled={renew.isPending}
              onClick={() => {
                setRenewing(null);
              }}
            >
              Cancel
            </button>
          </div>
        </SideDialog>
      )}
    </div>
  );
}

/** The three views as tabs: arrows, Home and End move between them. */
function TeamTabs({
  baseId,
  view,
  counts,
  onChange,
}: {
  baseId: string;
  view: View;
  counts: Partial<Record<View, number>>;
  onChange: (next: View) => void;
}) {
  const refs = useRef<Partial<Record<View, HTMLButtonElement | null>>>({});

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = VIEWS.findIndex((v) => v.id === view);
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = (at + 1) % VIEWS.length;
    else if (event.key === 'ArrowLeft') next = (at - 1 + VIEWS.length) % VIEWS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = VIEWS.length - 1;
    if (next === null) return;
    const target = VIEWS[next];
    if (target === undefined) return;
    event.preventDefault();
    onChange(target.id);
    refs.current[target.id]?.focus();
  };

  return (
    <div className="team-tabs" role="tablist" aria-label="Team" onKeyDown={onKeyDown}>
      {VIEWS.map((v) => (
        <button
          key={v.id}
          ref={(el) => {
            refs.current[v.id] = el;
          }}
          type="button"
          role="tab"
          id={`${baseId}-tab-${v.id}`}
          aria-selected={view === v.id}
          aria-controls={`${baseId}-panel`}
          tabIndex={view === v.id ? 0 : -1}
          className="team-tab"
          data-testid={`team-view-${v.id}`}
          onClick={() => {
            onChange(v.id);
          }}
        >
          <Icon name={v.icon} size={17} />
          <span>{v.label}</span>
          {counts[v.id] === undefined ? null : <span className="team-tab-count">{String(counts[v.id])}</span>}
        </button>
      ))}
    </div>
  );
}

function MembersBody({
  members,
  query,
  people,
  me,
  manageRefusal,
  canInvite,
  onInvite,
  onClearSearch,
  onChangeRole,
  onSeeRole,
}: {
  members: { data: Member[] | undefined; isPending: boolean; isError: boolean; error: unknown };
  query: string;
  people: Parameters<typeof MemberList>[0]['people'];
  me: number | null;
  manageRefusal: string | null;
  canInvite: boolean;
  onInvite: () => void;
  onClearSearch: () => void;
  onChangeRole: (member: Member) => void;
  onSeeRole: (role: string) => void;
}) {
  if (members.isPending) return <p className="team-quiet">Loading the team…</p>;
  if (members.isError || members.data === undefined) {
    return (
      <div className="ui-panel-body">
        <Callout tone="danger" title="The team could not be loaded" testId="team-members-error">
          {apiReason(members.error, 'Try again in a moment.')}
        </Callout>
      </div>
    );
  }

  const needle = query.trim().toLowerCase();
  const shown =
    needle === ''
      ? members.data
      : members.data.filter((m) =>
          [memberName(people, m.userId) ?? '', roleLabel(m.authorizationRole), m.productionTitle ?? '']
            .join(' ')
            .toLowerCase()
            .includes(needle),
        );

  if (shown.length === 0) {
    return (
      <div className="ui-panel-body">
        <EmptyState
          testId="team-search-empty"
          title={`Nobody on the team matches “${query.trim()}”`}
          action={
            <button type="button" className="ctl" data-testid="team-search-clear" onClick={onClearSearch}>
              Clear search
            </button>
          }
        />
      </div>
    );
  }

  return (
    <>
      <MemberList
        members={shown}
        people={people}
        me={me}
        manageRefusal={manageRefusal}
        onChangeRole={onChangeRole}
        onSeeRole={onSeeRole}
      />
      {/* Somebody alone on a production is the case this page exists for. */}
      {members.data.length === 1 && needle === '' && canInvite ? (
        <div className="team-solo" data-testid="team-solo">
          <p>
            It’s just you so far. Invite the location manager, the scout and anyone else working on this production.
          </p>
          <button type="button" className="ctl" data-testid="team-solo-invite" onClick={onInvite}>
            Invite teammate
          </button>
        </div>
      ) : null}
    </>
  );
}

function filterOptions(
  invitations: readonly Invitation[] | undefined,
  now: number,
): readonly FilterOption<InvitationFilter>[] {
  const count = (state: InvitationState | null): number | undefined =>
    invitations === undefined
      ? undefined
      : state === null
        ? invitations.length
        : invitations.filter((i) => invitationState(i, now) === state).length;
  const withCount = (id: InvitationFilter, label: string, state: InvitationState | null, attention = false) => {
    const n = count(state);
    return {
      id,
      label,
      ...(n === undefined ? {} : { count: n }),
      ...(attention && n !== undefined && n > 0 ? { tone: 'attention' as const } : {}),
    };
  };
  return [
    withCount('all', 'All', null),
    withCount('pending', 'Pending', 'PENDING'),
    withCount('expired', 'Expired', 'EXPIRED', true),
    withCount('accepted', 'Accepted', 'ACCEPTED'),
    withCount('revoked', 'Revoked', 'REVOKED'),
  ];
}

function InvitationsBody({
  invitations,
  filter,
  now,
  rights,
  actions,
  canInvite,
  onInvite,
}: {
  invitations: { data: Invitation[] | undefined; isPending: boolean; isError: boolean; error: unknown };
  filter: InvitationFilter;
  now: number;
  rights: InvitationRights;
  actions: InvitationActions;
  canInvite: boolean;
  onInvite: () => void;
}) {
  if (invitations.isPending) return <p className="team-quiet">Loading invitations…</p>;
  if (invitations.isError || invitations.data === undefined) {
    return (
      <div className="ui-panel-body">
        <Callout tone="danger" title="The invitations could not be loaded" testId="team-invitations-error">
          {apiReason(invitations.error, 'Try again in a moment.')}
        </Callout>
      </div>
    );
  }

  if (invitations.data.length === 0) {
    return (
      <div className="ui-panel-body">
        <EmptyState
          testId="team-invitations-empty"
          title="No invitations yet"
          action={
            canInvite ? (
              <button type="button" className="ctl" data-testid="team-invitations-invite" onClick={onInvite}>
                Invite teammate
              </button>
            ) : undefined
          }
        >
          Everyone you invite to this production is listed here, with where their invitation stands.
        </EmptyState>
      </div>
    );
  }

  const shown =
    filter === 'all'
      ? invitations.data
      : invitations.data.filter((i) => invitationState(i, now) === FILTER_STATE[filter]);

  if (shown.length === 0) {
    return (
      <div className="ui-panel-body">
        <EmptyState testId="team-invitations-filter-empty" title="No invitations in this list" />
      </div>
    );
  }

  return (
    <ul className="team-invite-list" data-testid="team-invitation-list">
      {shown.map((invitation) => (
        <InvitationRow key={invitation.id} invitation={invitation} now={now} rights={rights} actions={actions} />
      ))}
    </ul>
  );
}

/** Change a member's role: the job, what it may change, and nothing else (the credit stays). */
function ChangeRoleDialog({
  member,
  name,
  pending,
  error,
  onSave,
  onClose,
}: {
  member: Member;
  name: string;
  pending: boolean;
  error: string | null;
  onSave: (role: string) => void;
  onClose: () => void;
}) {
  const known = (AUTHORIZATION_ROLES as readonly string[]).includes(member.authorizationRole);
  const [role, setRole] = useState(known ? member.authorizationRole : 'LOCATION_MANAGER');
  const unchanged = role === member.authorizationRole;

  return (
    <SideDialog title="Change role" testId="change-role-dialog" onClose={onClose}>
      <p className="team-dialog-lede" data-testid="change-role-current">
        {name} is {roleLabel(member.authorizationRole)} on this production.
      </p>
      <form
        className="team-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!unchanged && !pending) onSave(role);
        }}
      >
        <div className="team-field">
          <label className="flbl" htmlFor="change-role-select">
            New role
          </label>
          <RoleSelect id="change-role-select" testId="change-role-select" value={role} onChange={setRole} startHere />
          <p className="team-field-help" data-testid="change-role-meaning">
            {roleMeaning(role)}
          </p>
        </div>
        <p className="team-field-help">The credit stays as it is. The change applies to this production only.</p>
        {error === null ? null : (
          <Callout tone="danger" title="Role not changed" testId="change-role-error">
            {error}
          </Callout>
        )}
        <div className="team-dialog-actions">
          <button type="submit" className="btn btn-primary" data-testid="change-role-save" disabled={pending || unchanged}>
            {pending ? 'Saving…' : 'Save role'}
          </button>
          <button type="button" className="ctl" data-testid="change-role-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </SideDialog>
  );
}

const REQUEST_FILTER_STATUSES: Readonly<Record<RequestFilter, readonly JoinRequestRow['status'][] | null>> = {
  pending: ['PENDING_APPROVAL'],
  decided: ['APPROVED_AWAITING_ACTIVATION', 'APPROVED', 'REJECTED'],
  all: null,
};

function requestFilterOptions(rows: readonly JoinRequestRow[] | undefined): readonly FilterOption<RequestFilter>[] {
  const count = (id: RequestFilter): number | undefined => {
    if (rows === undefined) return undefined;
    const statuses = REQUEST_FILTER_STATUSES[id];
    return statuses === null ? rows.length : rows.filter((r) => statuses.includes(r.status)).length;
  };
  const option = (id: RequestFilter, label: string, attention = false): FilterOption<RequestFilter> => {
    const n = count(id);
    return {
      id,
      label,
      ...(n === undefined ? {} : { count: n }),
      ...(attention && n !== undefined && n > 0 ? { tone: 'attention' as const } : {}),
    };
  };
  return [option('pending', 'Pending approval', true), option('decided', 'Decided'), option('all', 'All')];
}

/** The requests made through the invitation link, as the owner decides them. */
function JoinRequestsBody({
  query,
  filter,
  onApprove,
  onReject,
  onShareLink,
}: {
  query: {
    data: JoinRequestAnswer | undefined;
    isPending: boolean;
    isError: boolean;
    error: unknown;
  };
  filter: RequestFilter;
  onApprove: (request: JoinRequestRow) => void;
  onReject: (request: JoinRequestRow) => void;
  onShareLink: (() => void) | null;
}) {
  if (query.isPending) return <p className="team-quiet">Loading requests…</p>;
  if (query.isError || query.data === undefined) {
    return (
      <div className="ui-panel-body">
        <Callout tone="danger" title="The requests could not be loaded" testId="team-join-requests-error">
          {apiReason(query.error, 'Try again in a moment.')}
        </Callout>
      </div>
    );
  }

  const { requests, viewer, peopleAvailable } = query.data;
  const statuses = REQUEST_FILTER_STATUSES[filter];
  const shown = statuses === null ? requests : requests.filter((r) => statuses.includes(r.status));

  return (
    <>
      {viewer.canDecide || requests.every((r) => r.status !== 'PENDING_APPROVAL') ? null : (
        <p className="team-hint team-requests-hint" data-testid="team-join-requests-refusal">
          {viewer.refusal}
        </p>
      )}
      {peopleAvailable ? null : (
        <p className="team-hint team-requests-hint" data-testid="team-join-requests-people-unavailable">
          Names and addresses could not be loaded just now; requests are listed by number.
        </p>
      )}
      {requests.length === 0 ? (
        <div className="ui-panel-body">
          <EmptyState
            testId="team-join-requests-empty"
            title="No requests to join yet"
            action={
              onShareLink === null ? undefined : (
                <button type="button" className="ctl" data-testid="team-join-requests-share" onClick={onShareLink}>
                  Share invite link
                </button>
              )
            }
          >
            When someone asks to join through your invitation link, they are listed here for the production’s owner to
            approve or reject.
          </EmptyState>
        </div>
      ) : shown.length === 0 ? (
        <div className="ui-panel-body">
          <EmptyState testId="team-join-requests-filter-empty" title="No requests in this list" />
        </div>
      ) : (
        <JoinRequestList
          requests={shown}
          canDecide={viewer.canDecide}
          refusal={viewer.refusal}
          onApprove={onApprove}
          onReject={onReject}
        />
      )}
    </>
  );
}
